import { RegularButton } from "@components/button"
import { Modal } from "@components/modal"
import { FlexBox } from "@components/wrapper"
import { CareerCategory, PoolForm } from "@interfaces/career"
import { SelectOption } from "@interfaces/misc"
import CategoryServices from "@lib/api/services/category.services"
import FormServices from "@lib/api/services/form.services"
import { FormField, FormWrapper } from "@modules/career-form/components"
import { useFileForm, usePersonalOptions } from "@modules/career-form/hooks"
import clsx from "clsx"
import { Form, Formik } from "formik"
import { useEffect, useState } from "react"
import { FaCheck } from "react-icons/fa"
import { poolFormValidation } from "../constants/validations"

const initialValues: PoolForm = {
  career_position_id: "",
  category_id: "",
  user_fullname: "",
  user_email: "",
  user_phone_number: "",
  user_linkedin: "",
  user_university: "",
  user_city: "",
  user_activities: [],
  user_description: "",
  user_skills: [],
  user_skill_tools: [],
  job_question_answer: {
    other_question_1: "",
    other_question_2: "",
    other_question_3: "",
    other_question_4: "",
  },
  user_job_system: "",
  user_job_type: "",
  user_portfolio_link: "",
  user_expected_salary: [],
  user_applicant_type: "TALENT_POOL",
}

interface Skills {
  id: number
  skill_name: string
  skill_slug: string
}

interface Tools {
  id: number
  tool_name: string
  tool_slug: string
}

function PoolFormPage() {
  const { resume, errorResume, setErrorResume, handleUploadFile } = useFileForm()
  const { uniOptions, regencyOptions } = usePersonalOptions()

  const [validationRequired, setValidationRequired] = useState(false)
  const [skillOptions, setSkillOptions] = useState<SelectOption[]>([])
  const [toolOptions, setToolOptions] = useState<SelectOption[]>([])
  const [categoryOptions, setCategoryOptions] = useState<SelectOption[]>([])
  const [careerCategory, setCareerCategory] = useState<CareerCategory[]>([])
  const [errorMessage, setErrorMessage] = useState<string>("")
  const [modal, setModal] = useState<boolean>(false)

  const category = new CategoryServices()
  const form = new FormServices()

  useEffect(() => {
    const getSkills = async () => {
      try {
        const res = await category.getSkills()
        if (res?.isSuccess) {
          const result = res.getValue()
          const options: SelectOption[] = result.map((value: Skills) => {
            return {
              label: value.skill_name,
              value: value.id,
            }
          })
          setSkillOptions(options)
        }
      } catch (e) {
        console.log(e)
      }
    }

    const getTools = async () => {
      try {
        const res = await category.getTools()
        if (res?.isSuccess) {
          const result = res.getValue()
          const options: SelectOption[] = result.map((value: Tools) => {
            return {
              label: value.tool_name,
              value: value.id,
            }
          })
          setToolOptions(options)
        }
      } catch (e) {
        console.log(e)
      }
    }

    const getCareerCategories = async () => {
      try {
        const res = await category.getCategoryList()
        if (res?.isSuccess) {
          const result = res.getValue()
          setCareerCategory(result)
          const options: SelectOption[] = result.map((value: CareerCategory) => {
            return {
              label: value.category_name,
              value: value.id,
            }
          })
          setCategoryOptions(options)
        }
      } catch (e) {
        console.log(e)
      }
    }

    getSkills()
    getTools()
    getCareerCategories()
  }, [])

  function handleSubmit() {
    setValidationRequired(true)

    if (!resume) {
      setErrorResume("*Wajib sertakan file")
    }
  }

  const handleClose = async () => {
    setModal(false)
    window.location.assign("/")
  }

  return (
    <FormWrapper head="Form Talent Pool - Harisenin.com">
      <Formik
        initialValues={initialValues}
        validationSchema={poolFormValidation}
        validateOnChange={validationRequired}
        validateOnBlur={validationRequired}
        onSubmit={async (values, { setFieldError }) => {
          const filteredActivities = values.user_activities.filter((v) => v)
          const filteredSalaries = values.user_expected_salary.filter((v) => v)

          if (!filteredActivities.length || !filteredSalaries.length) {
            if (!filteredActivities.length) {
              setFieldError("user_activities", "*Kegiatan wajib diisi")
            }

            if (!filteredSalaries.length) {
              setFieldError("user_expected_salary", "*Ekspetasi gaji wajib diisi")
            }

            return
          }

          const valuesTemp: PoolForm = {
            ...values,
            user_applicant_type: "TALENT_POOL",
            user_activities: filteredActivities,
            user_expected_salary: filteredSalaries,
          }

          if (!resume) {
            setErrorResume("*Wajib sertakan file")
            return setErrorMessage("File CV wajib di upload")
          }

          const postFormPool = await form.postFormPool(valuesTemp)

          if (postFormPool.isFailure) {
            return setErrorMessage("Terjadi Kesalahan. Silahkan Coba Lagi")
          }

          const resultPost = postFormPool.getValue()
          const bodyFormData = new FormData()
          bodyFormData.append("id", resultPost.id)
          bodyFormData.append("resume", resume)
          const uploadAsset = await form.postAssetForm(bodyFormData)

          if (uploadAsset.isFailure) {
            return setErrorMessage("Terjadi Kesalahan, Asset Tidak Terupload")
          }

          setModal(true)
        }}
      >
        {({ values, errors, isSubmitting }) => {
          const rawPositionData = careerCategory.filter((value) => {
            return value.id == values.category_id
          })

          const rawPositionOptions = rawPositionData.map((value: CareerCategory) => {
            return value.career_positions
          })
          const positionOptions: any[] = rawPositionOptions.map((value: any) => {
            return value.map((value: any) => {
              return {
                label: value.position_name,
                value: value.id,
              }
            })
          })
          const fixPositionOptions: SelectOption[] = positionOptions[0]

          return (
            <Form>
              {/* <pre>{JSON.stringify(values, null, 2)}</pre>
              <pre>{JSON.stringify(errors, null, 2)}</pre> */}
              <FormWrapper.PersonalInfo>
                <FormField.Select
                  name="category_id"
                  label="Tim"
                  placeholder="Pilih Tim"
                  options={categoryOptions}
                  noOptionsMessage="Tidak ada kota/kabupaten yang ditemukan"
                />

                <FormField.Select
                  name="career_position_id"
                  label="Posisi"
                  placeholder="Pilih Posisi"
                  options={fixPositionOptions}
                  noOptionsMessage="Tidak ada posisi yang ditemukan"
                />

                <FormField.Input
                  name="user_fullname"
                  label="Nama Lengkap"
                  placeholder="Masukkan nama lengkap kamu"
                />

                <FormField.Input
                  name="user_email"
                  label="Email"
                  placeholder="Masukkan email kamu"
                />

                <FormField.Phone
                  name="user_phone_number"
                  label="Nomor Whatsapp"
                  placeholder="Masukkan nomor Whatsapp kamu"
                />

                <FormField.Input
                  name="user_linkedin"
                  label="Linkedin"
                  placeholder="Masukkan link Linkedin kamu"
                />

                <FormField.Input
                  name="user_portfolio_link"
                  label="Link Portofolio"
                  placeholder="Masukkan link portfolio kamu"
                />

                <FormField.FileUpload
                  label="CV/Resume"
                  placeholder="Unggah File CV / Resume kamu"
                  onUpload={handleUploadFile}
                  fileState={resume}
                  errorMessage={errorResume}
                />

                <FormField.CreatableSelect
                  name="user_university"
                  label="Asal Universitas"
                  placeholder="Masukkan Universitas kamu"
                  options={uniOptions}
                  noOptionsMessage="Tidak ada universitas yang ditemukan"
                />

                <FormField.Select
                  name="user_city"
                  label="Domisili"
                  placeholder="Masukkan kota/kabupaten kamu"
                  options={regencyOptions}
                  noOptionsMessage="Tidak ada kota/kabupaten yang ditemukan"
                />

                <FormField.CheckRow
                  name="user_activities"
                  options={["Kuliah", "Mencari Kerja", "Sudah Bekerja"]}
                  label="Kegiatan Saat ini"
                  hasCreatable
                />

                <FormField.TextArea
                  name="user_description"
                  label="Deskripsi Diri"
                  placeholder="Ceritakan diri anda secara seingkat"
                />

                <FormField.RadioRow
                  name="user_job_system"
                  options={["WFO", "WFH", "Hybrid"]}
                  label="Sistem Kerja"
                />

                <FormField.RadioRow
                  name="user_job_type"
                  options={["Full-time", "Part-time", "Internship"]}
                  label="Tipe Kerja"
                />
              </FormWrapper.PersonalInfo>

              <FormWrapper.OtherQuestion>
                <FormField.TextArea
                  name="job_question_answer.other_question_1"
                  label="Apa pengalaman dan pencapaian Anda yang berhubungan dengan posisi ini?"
                  placeholder="Tulis Jawaban Kamu"
                />

                <FormField.TextArea
                  name="job_question_answer.other_question_2"
                  label="Jika nanti Anda terpilih, apa ekspektasi Anda terhadap posisi ini?"
                  placeholder="Tulis Jawaban Kamu"
                />

                <FormField.TextArea
                  name="job_question_answer.other_question_3"
                  label="Jika nanti Anda terpilih, apa kontribusi yang akan Anda berikan?"
                  placeholder="Tulis Jawaban Kamu"
                />

                <FormField.TextArea
                  name="job_question_answer.other_question_4"
                  label="Jika nanti Anda terpilih, seberapa besar komitmen Anda terhadap posisi ini?"
                  placeholder="Tulis Jawaban Kamu"
                />

                <FormField.MultipleSelect
                  name="user_skills"
                  label="Sebutkan skills yang Anda miliki berhubungan dengan posisi ini?"
                  placeholder="Masukkan Skills"
                  options={skillOptions}
                  noOptionsMessage="Tidak ada skill yang ditemukan"
                />

                <FormField.MultipleSelect
                  name="user_skill_tools"
                  label="Apa tools atau aplikasi yang sering Anda gunakan terkait dengan posisi ini?"
                  placeholder="Masukkan Tools"
                  options={toolOptions}
                  noOptionsMessage="Tidak ada tool yang ditemukan"
                />

                <FormField.CheckRow
                  name="user_expected_salary"
                  options={[
                    "<1.000.000",
                    "Rp 1.500.000 - Rp 2.000.000",
                    "Rp 2.000.000 - Rp 2.500.000",
                    "> Rp 2.500.000",
                    "Negotiable",
                  ]}
                  label="Ekspektasi Gaji"
                  hasCreatable
                  isNotRow
                />
              </FormWrapper.OtherQuestion>
              <div className="flex justify-center mb-1">
                <small className="text-red lg:text-md ">{errorMessage}</small>
              </div>
              <FlexBox justify="center">
                <RegularButton
                  disabled={isSubmitting}
                  isSubmitting={isSubmitting}
                  className={clsx("uppercase sm:w-2/5 sm:h-[48px]", "w-full h-9")}
                  isFormik
                  onClick={handleSubmit}
                >
                  Kirim Data
                </RegularButton>
              </FlexBox>
            </Form>
          )
        }}
      </Formik>
      <Modal show={modal} onClose={handleClose} contentPadding="p-[30px]">
        <FlexBox direction="col" justify="center" className="gap-5" align="center">
          <FlexBox justify="center">
            <FlexBox
              className="bg-green-50 w-[100px] h-[100px] rounded-full"
              justify="center"
              align="center"
            >
              <FlexBox
                className="bg-green w-[60px] h-[60px] rounded-full"
                justify="center"
                align="center"
              >
                <FaCheck className="sm:text-2xl text-white font-semibold" />
              </FlexBox>
            </FlexBox>
          </FlexBox>

          <div className="text-center font-medium">Anda berhasil melamar dengan Talent Pool</div>

          <RegularButton onClick={handleClose} customPadding="py-2.5" className="w-2/5">
            Tutup
          </RegularButton>
        </FlexBox>
      </Modal>
    </FormWrapper>
  )
}

export default PoolFormPage
